import type { Direction } from '../types';
import type { UIMode } from '../context/UIModeContext';

/**
 * DirectionPicker - Movement direction selection
 *
 * Features:
 * - Visual direction indicators
 * - Feature gating for Pro directions
 * - Clear labels
 * - Simple mode: 3 directions (horizontal, vertical, diagonal)
 * - Advanced mode: 5 directions (add infinity, random)
 */

interface DirectionOption {
  value: Direction;
  label: string;
  icon: string;
  isPro: boolean;
}

const DIRECTIONS: DirectionOption[] = [
  { value: 'horizontal', label: 'Horizontal', icon: '↔️', isPro: false },
  { value: 'vertical', label: 'Vertical', icon: '↕️', isPro: true },
  { value: 'diagonal', label: 'Diagonal', icon: '↗️', isPro: true },
  { value: 'infinity', label: 'Infinity', icon: '∞', isPro: true },
  { value: 'random', label: 'Random', icon: '🔀', isPro: true },
];

// Simple mode directions
const SIMPLE_MODE_DIRECTIONS: Direction[] = ['horizontal', 'vertical', 'diagonal'];

interface DirectionPickerProps {
  value: Direction;
  onChange: (direction: Direction) => void;
  disabled?: boolean;
  isPro: boolean;
  mode: UIMode;
}

export function DirectionPicker({
  value,
  onChange,
  disabled = false,
  isPro,
  mode,
}: DirectionPickerProps): JSX.Element {
  // Filter directions based on mode
  const availableDirections = mode === 'simple'
    ? DIRECTIONS.filter(dir => SIMPLE_MODE_DIRECTIONS.includes(dir.value))
    : DIRECTIONS;

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Direction
      </label>

      <div className="flex flex-wrap gap-2">
        {availableDirections.map((direction) => {
          const isLocked = direction.isPro && !isPro;
          const isSelected = value === direction.value;

          return (
            <button
              key={direction.value}
              type="button"
              onClick={() => !isLocked && !disabled && onChange(direction.value)}
              disabled={disabled || isLocked}
              title={direction.label}
              className={`
                relative w-[60px] h-[60px] rounded border-2 transition-all flex items-center justify-center flex-shrink-0
                ${isSelected ? 'border-sync-blue bg-sync-blue/10' : 'border-gray-300 bg-white'}
                ${isLocked ? 'opacity-40 cursor-not-allowed' : 'hover:border-sync-blue cursor-pointer'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              <span className="text-2xl">{direction.icon}</span>
              {isLocked && <span className="absolute top-1 right-1 text-xs pro-icon"></span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
